import { devBus } from '../debug/devBus';

// The generator gate (N8). The shop has no electricity until the visitor
// pull-starts the generator — a global ONE-WAY latch, session-scoped (boot:
// off; refresh resets; no persistence by design — the ritual is the point).
//
// Consumers:
//  - InteractionEngine skips rules marked `requiresPower` while unpowered and
//    flips the latch via the `power.on` action (the generator's pull-start).
//  - SoundscapeEngine treats placements marked `requiresPower` as inactive
//    until powered — the shop's electrical voice wakes when the latch sets
//    (level-triggered beds fade in on their own; fluid, no hard cut).
//  - The view toggle disables "go inside" until powered (the dark shop refuses).
//
// Two-stage awakening: InteractionEngine.arm() already resumes the
// AudioContext on the FIRST pointerdown anywhere — that's stage one (the
// world: street, wind). The generator click is stage two (the shop) — and
// since it's also a pointerdown, the browser's audio-unlock gesture is
// diegetic when the generator is the visitor's first touch.
export interface PowerLatch {
  readonly powered: boolean;
  /** Latch on (idempotent). */
  on(): void;
  /** Latch off (idempotent). The shop CAN be powered down — item 3 gates this to
   *  the day's closing window in app.ts (a deliberate end-of-day ritual). */
  off(): void;
  /** Subscribe to any power flip (off→on or on→off). Returns an unsubscribe. */
  onChange(fn: () => void): () => void;
}

export function createPowerLatch(): PowerLatch {
  let powered = false;
  const listeners: Array<() => void> = [];
  const fire = (): void => { for (const fn of [...listeners]) fn(); };
  return {
    get powered() { return powered; },
    on() {
      if (powered) return;
      powered = true;
      fire();
      devBus.emit('power:on', {});
    },
    off() {
      if (!powered) return;
      powered = false;
      fire();
      devBus.emit('power:off', {});
    },
    onChange(fn) {
      listeners.push(fn);
      return () => {
        const i = listeners.indexOf(fn);
        if (i >= 0) listeners.splice(i, 1);
      };
    },
  };
}
